import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Plus, Users } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '../../../components/ui/Button';
import { Card, CardBody, CardHeader } from '../../../components/ui/Card';
import { ErrorState, PageContainer, PageHeader, TableSkeleton } from '../../../components/ui/Page';
import { useToast } from '../../../components/ui/Toast';
import { cn } from '../../../utils/cn';
import { useAuth } from '../../auth/AuthProvider';
import { teamApi, teamKeys } from '../../team/api';
import { BranchDialog } from '../../team/components/BranchDialog';
import { InviteStaffDialog } from '../../team/components/InviteStaffDialog';
import { tenantApi, tenantKeys } from '../api';
import { BrandingForm } from '../components/BrandingForm';
import { PreferencesForm } from '../components/PreferencesForm';
import { ProfileForm } from '../components/ProfileForm';

const STEPS: { key: string; title: string; description: string }[] = [
  { key: 'profile', title: 'Business profile', description: 'Name, type and how customers reach you.' },
  { key: 'branding', title: 'Branding', description: 'Your logo and colour for the app and emails.' },
  { key: 'branch', title: 'First branch', description: 'Where your team works. You can add more later.' },
  { key: 'staff', title: 'Invite your team', description: 'Send invitations now or skip and do it later.' },
  {
    key: 'notifications',
    title: 'Quiet hours',
    description: 'When non-urgent staff notifications should wait.',
  },
];

function BranchStep({ onDone }: { onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const branches = useQuery({ queryKey: teamKeys.branches, queryFn: () => teamApi.branches() });
  const count = branches.data?.data.length ?? 0;
  return (
    <div className="space-y-4">
      {count ? (
        <ul className="space-y-1 text-sm">
          {branches.data!.data.map((b) => (
            <li key={b.id} className="flex items-center gap-2">
              <Check aria-hidden className="size-4 text-success" /> {b.name}{' '}
              <code className="text-xs text-fg-subtle">{b.code}</code>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-fg-muted">No branches yet.</p>
      )}
      <div className="flex gap-2">
        <Button
          variant="secondary"
          leadingIcon={<Plus aria-hidden className="size-4" />}
          onClick={() => setOpen(true)}
        >
          {count ? 'Add another branch' : 'Create branch'}
        </Button>
        <Button onClick={onDone} disabled={!count}>
          Continue
        </Button>
      </div>
      <BranchDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}

function StaffStep({ onDone }: { onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const staff = useQuery({
    queryKey: teamKeys.staff({ pageSize: 50 }),
    queryFn: () => teamApi.staff({ pageSize: 50 }),
  });
  return (
    <div className="space-y-4">
      <ul className="space-y-1 text-sm">
        {(staff.data?.data ?? []).map((s) => (
          <li key={s.id} className="flex items-center gap-2">
            <Users aria-hidden className="size-4 text-fg-subtle" /> {s.fullName}{' '}
            <span className="text-fg-subtle">· {s.roles.map((r) => r.name).join(', ')}</span>
          </li>
        ))}
      </ul>
      <div className="flex gap-2">
        <Button
          variant="secondary"
          leadingIcon={<Plus aria-hidden className="size-4" />}
          onClick={() => setOpen(true)}
        >
          Invite someone
        </Button>
        <Button onClick={onDone}>Continue</Button>
      </div>
      <InviteStaffDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}

/** First-time setup (FR-TEN-03). Each step is saved independently and can be resumed. */
export function OnboardingPage() {
  const { reload } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const onboarding = useQuery({ queryKey: tenantKeys.onboarding, queryFn: tenantApi.onboarding });
  const [viewing, setViewing] = useState<string | null>(null);

  const complete = useMutation({
    mutationFn: (step: string) => tenantApi.completeStep(step),
    onSuccess: async (data) => {
      queryClient.setQueryData(tenantKeys.onboarding, data);
      setViewing(null);
      if (data.completed) {
        await reload();
        toast.success('Setup complete. Welcome aboard!');
        navigate('/app', { replace: true });
      }
    },
    onError: (err) => toast.error(err),
  });

  if (onboarding.isPending)
    return (
      <PageContainer width="md">
        <TableSkeleton />
      </PageContainer>
    );
  if (onboarding.isError)
    return (
      <PageContainer width="md">
        <ErrorState error={onboarding.error} onRetry={() => void onboarding.refetch()} />
      </PageContainer>
    );

  const current = onboarding.data.currentStep;
  const currentIndex = STEPS.findIndex((s) => s.key === current);
  const active = viewing ?? current;
  const step = STEPS.find((s) => s.key === active) ?? STEPS[0]!;
  const finish = () => complete.mutate(step.key);

  return (
    <PageContainer width="md">
      <PageHeader title="Set up your account" description="A few steps and your team is ready to go." />
      <ol className="mb-6 grid gap-2 sm:grid-cols-5" aria-label="Setup progress">
        {STEPS.map((s, i) => {
          const done = onboarding.data.completed || i < currentIndex;
          const isActive = s.key === active;
          return (
            <li key={s.key}>
              <button
                type="button"
                disabled={!done && s.key !== current}
                onClick={() => setViewing(s.key)}
                aria-current={isActive ? 'step' : undefined}
                className={cn(
                  'flex w-full flex-col gap-1 rounded-md border px-3 py-2 text-left text-xs disabled:cursor-not-allowed disabled:opacity-60',
                  isActive ? 'border-brand-600 bg-brand-50 dark:bg-brand-900/30' : 'border-line bg-surface',
                )}
              >
                <span className="flex items-center gap-1 font-medium text-fg">
                  {done ? (
                    <Check aria-hidden className="size-3.5 text-success" />
                  ) : (
                    <span className="tabular-nums">{i + 1}.</span>
                  )}
                  {s.title}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
      <Card>
        <CardHeader title={step.title} description={step.description} />
        <CardBody>
          {step.key === 'profile' ? <ProfileForm submitLabel="Save and continue" onSaved={finish} /> : null}
          {step.key === 'branding' ? (
            <div className="space-y-4">
              <BrandingForm submitLabel="Save and continue" onSaved={finish} />
              <Button variant="ghost" onClick={finish}>
                Skip for now
              </Button>
            </div>
          ) : null}
          {step.key === 'branch' ? <BranchStep onDone={finish} /> : null}
          {step.key === 'staff' ? <StaffStep onDone={finish} /> : null}
          {step.key === 'notifications' ? <PreferencesForm section="notifications" onSaved={finish} /> : null}
        </CardBody>
      </Card>
    </PageContainer>
  );
}
